import { useApolloClient } from "@apollo/client";
import { todayPT } from "@homebound/activesupport";
import { currentAuthToken } from "@homebound/auth-components";
import {
  BoundRadioGroupField,
  BoundSelectField,
  Button,
  Css,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useComputed,
  useModal,
  useSnackbar,
} from "@homebound/beam";
import { useMemo, useState } from "react";
import { FileField } from "src/components";
import { baseDownloadUrl } from "src/context";
import { BidContractType, BidPackageBidderKind, InternalEstimateType } from "src/generated/graphqlTypes";
import {
  BidPackageDetailPageDocument,
  PageBidPackageDetailFragment,
} from "src/routes/bids/BidPackageDetailPage.generated";
import { BidPackageDetailPage_TradeFragment } from "src/routes/bids/components/AddTradePartnerModal.generated";
import { useCreateBidContractMutation } from "src/routes/developments/procurement/components/CreateDevelopmentContractButton.generated";
import { queryResult } from "src/utils";
import { ObjectConfig, required, useFormState } from "src/utils/formState";
import { openInSelf } from "src/utils/window";
import { INTERNAL_ESTIMATE_ID } from "../PlanBasedBidPackagePage";
import { PlanBasedBidsTableV2_BidPlanChildGroupsDocument } from "./PlanBasedBidsTableV2.generated";
import { useUploadPlanBasedBidV2ModalQuery } from "./UploadPlanBasedBidV2Modal.generated";

type UploadPlanBasedBidV2ModalProps = { bidPackage: PageBidPackageDetailFragment };

export function UploadPlanBasedBidV2Modal({ bidPackage }: UploadPlanBasedBidV2ModalProps) {
  const query = useUploadPlanBasedBidV2ModalQuery({
    variables: { costCodes: bidPackage.costCodes.map((cc) => cc.id), markets: [bidPackage.relevantMarket.id] },
  });
  return queryResult(query, ({ tradePartners }) => (
    <UploadPlanBasedBidV2ModalView bidPackage={bidPackage} availableTradePartners={tradePartners} />
  ));
}

type UploadPlanBasedBidV2ModalViewProps = {
  bidPackage: PageBidPackageDetailFragment;
  availableTradePartners: BidPackageDetailPage_TradeFragment[];
};

function UploadPlanBasedBidV2ModalView({ bidPackage, availableTradePartners }: UploadPlanBasedBidV2ModalViewProps) {
  const [errors, setErrors] = useState<string[]>([]);
  const [erroredUploadingXlsx, setErroredUploadingXlsx] = useState(false);
  const [createdBidContractId, setCreatedBidContractId] = useState<string>();
  const [loading, setLoading] = useState(false);
  const { triggerNotice } = useSnackbar();
  const { closeModal } = useModal();
  const client = useApolloClient();
  const [createBidContract] = useCreateBidContractMutation();

  const bidders = bidPackage.activeBidders;
  // Offer the internal-estimate option only when the package doesn't already have one
  // once linked, it appears via `bidders` instead.
  const showInternalEstimate = !bidPackage.internalEstimateBidContract;
  // Union of bidders (trade partners or estimate) already on the package, matching trades
  // from the query, and the synthetic internal-estimate option when applicable.
  const bidderOptions = useMemo(() => {
    const requestedTpIds = bidPackage.requests.map((r) => r.tradePartner.id).unique();
    return [
      ...bidders,
      // Explicitly exclude trades already on a request to prevent including an archived trade.
      ...availableTradePartners.filter((tp) => !requestedTpIds.includes(tp.id)),
      ...(showInternalEstimate ? [{ id: INTERNAL_ESTIMATE_ID, name: "Homebound Internal Estimate" }] : []),
    ];
  }, [availableTradePartners, bidPackage.requests, bidders, showInternalEstimate]);

  const formState = useFormState({
    config: formConfig,
    init: {
      onlyOnce: true,
      input: {
        bidder: bidderOptions[0]?.id,
        // If the synthetic internal-estimate option is pre-selected (only option),
        // default the estimate type so the form is submittable without manual change.
        internalEstimateType:
          bidderOptions[0]?.id === INTERNAL_ESTIMATE_ID ? InternalEstimateType.Temporary : undefined,
      },
    },
  });

  const showInternalEstimateType = useComputed(() => formState.bidder.value === INTERNAL_ESTIMATE_ID, [formState]);
  const file = useComputed(() => formState.file.value, [formState]);
  const uploadDisabled = useComputed(() => !formState.bidder.value || !formState.file.value, [formState]);

  const onDownloadTemplate = () => {
    const selected = bidders.find((b) => b.id === formState.bidder.value);
    const params = new URLSearchParams({ type: "planBasedBidPackageV2", bidPackageId: bidPackage.id });
    // Two pre-fill paths: trade-partner bidders route through BidPackageRequest, while the
    // internal-estimate bidder has no request and pre-fills directly from its latest BCR.
    if (selected?.kind === BidPackageBidderKind.TradeRequest && selected.request) {
      params.set("bidPackageRequestId", selected.request.id);
    } else if (selected?.kind === BidPackageBidderKind.InternalEstimate && selected.bidContract) {
      params.set("bidContractRevisionId", selected.bidContract.latestRevision.id);
    }
    openInSelf(`${baseDownloadUrl()}/xlsx?${params.toString()}`);
  };

  async function onUpload() {
    if (loading) return;
    setLoading(true);
    try {
      const selectedId = formState.bidder.value;
      const selected = bidders.find((b) => b.id === selectedId);
      const isNewInternalEstimate = selectedId === INTERNAL_ESTIMATE_ID;
      // No match in `bidders` (and not the synthetic internal-estimate option) means the user picked
      // a synthetic new-trade option whose id is a TradePartner id.
      const newTradePartnerId = !selected && !isNewInternalEstimate ? selectedId : undefined;
      let bidContractId = selected?.bidContract?.id;
      // For trade-partner bidders, the bid contract may not exist yet — create it before uploading.
      // Same for the new internal-estimate option (no contract yet, so create one with isInternalEstimate).
      // For an existing internal-estimate bidder, the bid contract always exists (activeBidders only
      // emits the estimate entry when bidPackage.internalEstimateBidContract is linked), so we skip creation.
      if (!erroredUploadingXlsx && !bidContractId && selected?.kind !== BidPackageBidderKind.InternalEstimate) {
        const { data, errors: gqlErrors } = await createBidContract({
          variables: {
            input: isNewInternalEstimate
              ? {
                  isInternalEstimate: true,
                  internalEstimateBidPackageId: bidPackage.id,
                  internalEstimateType: formState.internalEstimateType.value,
                  startDate: todayPT(),
                  type: BidContractType.UserCreatedGroupBased,
                }
              : {
                  tradePartnerId: selected?.tradePartner?.id ?? newTradePartnerId,
                  // For an existing request, link the bid contract directly. For a new trade,
                  // pass bidPackageId so it finds/creates the request.
                  bidRequestId: selected?.request?.id,
                  bidPackageId: selected?.request ? undefined : bidPackage.id,
                  startDate: todayPT(),
                  type: BidContractType.UserCreatedGroupBased,
                },
          },
          errorPolicy: "ignore",
        });
        bidContractId = data?.saveBidContract.bidContract.id;
        if (!bidContractId) {
          triggerNotice({ message: gqlErrors?.join(",") ?? "Bid Contract Creation failed" });
          return;
        }
        setCreatedBidContractId(bidContractId);
      }

      const response = await fetch(
        `${baseDownloadUrl()}/xlsx?type=bidContractPlanBasedV2&bidContractId=${createdBidContractId ?? bidContractId}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${await currentAuthToken()}` },
          body: formState.value.file,
        },
      );

      if (response.status !== 200) {
        setErroredUploadingXlsx(true);
        const { message } = await response.json();
        if (message) setErrors([message]);
        return;
      }

      await client.refetchQueries({
        include: [BidPackageDetailPageDocument, PlanBasedBidsTableV2_BidPlanChildGroupsDocument],
      });
      triggerNotice({ message: "Bid uploaded successfully" });
      closeModal();
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <ModalHeader>Upload Bid</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gray700.gap2.$}>
          <div>
            Import trade costs from the downloaded{" "}
            <span css={Css.blue600.cursorPointer.fw6.$} data-testid="xlsxTemplateLink" onClick={onDownloadTemplate}>
              .xlsx bid sheet
            </span>{" "}
            template.
          </div>
          <div>New uploaded costs will override any existing cost entries for the selected trade.</div>
          <div>To retain costs, leave those cost cells blank.</div>
          <BoundSelectField
            field={formState.bidder}
            label="Trade"
            options={bidderOptions}
            disabled={!!createdBidContractId}
            onSelect={(v) => {
              // Default estimate type to Temporary when picking the internal estimate for the first time
              if (v === INTERNAL_ESTIMATE_ID && !formState.internalEstimateType.value) {
                formState.internalEstimateType.set(InternalEstimateType.Temporary);
              }
              formState.bidder.set(v);
            }}
          />
          {showInternalEstimateType && (
            <BoundRadioGroupField
              label="Estimate Type"
              layout="horizontal"
              options={[
                { label: "Temporary", value: InternalEstimateType.Temporary },
                { label: "Lot Determined", value: InternalEstimateType.LotDetermined },
              ]}
              field={formState.internalEstimateType}
            />
          )}
          <div css={Css.df.fdc.$}>
            <FileField
              label="Drag & drop or click to browse files"
              allowedFileTypes={[
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                "application/vnd.ms-excel",
              ]}
              file={file}
              setFile={(file) => {
                formState.set({ file });
                setErrors([]);
              }}
              errors={errors}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="tertiary" onClick={closeModal} />
        <Button label="Upload" disabled={loading || uploadDisabled || !!errors.length} onClick={onUpload} />
      </ModalFooter>
    </>
  );
}

type FormValues = { bidder?: string | null; file?: File | null; internalEstimateType?: InternalEstimateType | null };

const formConfig: ObjectConfig<FormValues> = {
  bidder: { type: "value", rules: [required] },
  file: { type: "value", rules: [required] },
  internalEstimateType: { type: "value" },
};
